import { useCallback, useMemo } from 'react';
import { isHttpBackendError } from 'shell/error/definitions/http-backend-error';
import useFetch from 'use-http';
import CancelablePromise, { cancelable } from 'cancelable-promise';
import { UseHttpFetchObjectReturn, HttpFetchData, UseHttpFetchArgs } from './types';

function makeCancelable<T>(reqPromise: Promise<T>): CancelablePromise<T | void> {
  const cancelablePromise = cancelable(reqPromise);
  return cancelablePromise.catch((error) => {
    // Cancel the promise if the error was globally handled, otherwise propagate the error.
    if (isHttpBackendError(error) && error.globallyHandled) {
      return cancelablePromise.cancel();
    }
    // Rethrow. At this stage the throw will have no effect if the promise has been cancelled.
    return Promise.reject(error);
  });
}

/**
 * Just a decorator around the "useFetch" hook. It catches globally handled errors avoiding
 * to propagates them.
 */
export function useHttpFetch<TData>(...args: UseHttpFetchArgs): UseHttpFetchObjectReturn<TData> {
  const { get, put, post, patch, delete: httpDelete, del, ...rest } = useFetch(...args);

  const catchedGet = useCallback((route?: string) => makeCancelable(get(route)), [get]);
  const catchedPut: HttpFetchData = useCallback(
    (route, data) => makeCancelable(put(route, data)),
    [put]
  );
  const catchedPost: HttpFetchData = useCallback((data) => makeCancelable(post(data)), [post]);
  const catchedPatch: HttpFetchData = useCallback((data) => makeCancelable(patch(data)), [patch]);
  const catchedDelete: HttpFetchData = useCallback(
    (route, data) => makeCancelable(httpDelete(route, data)),
    [httpDelete]
  );
  const catchedDel: HttpFetchData = useCallback((data) => makeCancelable(del(data)), [del]);

  const publicApi = useMemo(
    () => ({
      get: catchedGet,
      put: catchedPut,
      post: catchedPost,
      patch: catchedPatch,
      delete: catchedDelete,
      del: catchedDel,
      ...rest,
    }),
    [catchedGet, catchedPut, catchedPost, catchedPatch, catchedDelete, rest]
  );

  return publicApi;
}
